import { Building2, LogOut, Palette } from "lucide-react";
import { useEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { ErrorBoundary } from "@/components/ErrorBoundary";
import { IconButton } from "@/components/kit";
import { useAuth } from "@/lib/auth";
import { useIsDesktop } from "@/lib/hooks";
import { cn } from "@/lib/utils";

export function Wordmark({ className }) {
  return <span className={cn("text-[13px] font-semibold uppercase tracking-[0.25em] text-primary", className)}>ListingReel</span>;
}

const NAV = [
  { to: "/projects", label: "Properties", icon: Building2, testId: "nav-properties-link" },
  { to: "/branding", label: "Branding", icon: Palette, testId: "open-branding-button" },
];

function Sidebar() {
  const { user, signOut } = useAuth();
  return (
    <aside className="fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-border bg-[hsl(226_62%_8%)] px-5 py-8" data-testid="app-sidebar">
      <Wordmark className="px-3" />
      <p className="mt-2 px-3 text-xs text-muted-foreground">Branded listing videos</p>
      <nav className="mt-10 flex flex-col gap-1" aria-label="Main">
        {NAV.map(({ to, label, icon: Icon, testId }) => (
          <NavLink key={to} to={to} data-testid={testId}
            className={({ isActive }) => cn("focus-ring flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors",
              isActive ? "bg-primary/15 text-primary" : "text-secondary-foreground hover:bg-card hover:text-foreground")}>
            <Icon className="h-5 w-5" aria-hidden /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto border-t border-border pt-5">
        {user ? (
          <div className="mb-3 px-3" data-testid="sidebar-user">
            <p className="truncate text-sm font-medium text-foreground">{user.name}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          </div>
        ) : null}
        <button type="button" onClick={signOut} data-testid="sign-out-button"
          className="focus-ring flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-sm font-medium text-secondary-foreground transition-colors hover:bg-card hover:text-foreground">
          <LogOut className="h-5 w-5" aria-hidden /> Sign out
        </button>
      </div>
    </aside>
  );
}

function TopBar() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="glass sticky top-0 z-30 flex h-16 items-center gap-1 border-b border-border px-4" data-testid="app-topbar">
      <NavLink to="/projects" className="focus-ring mr-auto rounded-md" data-testid="topbar-home-link"><Wordmark /></NavLink>
      <IconButton icon={Palette} label="Branding" onClick={() => navigate("/branding")} data-testid="open-branding-button" />
      <IconButton icon={LogOut} label="Sign out" onClick={signOut} data-testid="sign-out-button" />
    </div>
  );
}

export function AppShell() {
  const desktop = useIsDesktop();
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <div className="min-h-screen bg-background">
      {desktop ? <Sidebar /> : <TopBar />}
      <main className="lg:pl-64">
        <div className="mx-auto w-full max-w-6xl px-4 pb-44 pt-4 sm:px-6 lg:px-10 lg:pt-10">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
